import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ProfileData, Profile } from "../services/api";
import { MacroTargeting } from "./MacroTargeting";
import { useToast } from "../contexts/ToastContext";
import { LLMMacros } from "../types/common";
import { NumberInput } from "./NumberInput";

interface ProfileFormProps {
    profile?: Profile;
    isMainProfile?: boolean;
    onSave: (profileData: ProfileData) => Promise<void>;
    onDelete?: () => Promise<void>;
    loading?: boolean;
}

interface ExtendedProfileData extends ProfileData {
    // Activity & Fitness
    activity_level?: string;
    exercise_frequency?: string;
    exercise_types?: string[];
    fitness_goals?: string[];
    
    // Health Conditions
    health_conditions?: string[];
    medications?: string[];
    supplements?: string[];
    
    // Meal Preferences
    cost_preference?: string;
    meal_timing_preference?: string;
    snacking_preference?: string;
    cooking_time_available?: string;
    cooking_skill_level?: string;
    
    // Food Preferences Extended
    cuisine_preferences?: string[];
    flavor_preferences?: string[];
    texture_preferences?: string[];
    cooking_methods?: string[];
    
    // Lifestyle
    occupation_type?: string;
    stress_level?: string;
    sleep_hours?: number;
    water_intake?: number;
    
    // Goals
    primary_goal?: string;
    target_weight?: number;
    target_timeline?: string;
    
    // Macro Targets
    macro_targets?: Partial<LLMMacros>;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
    profile,
    isMainProfile = false,
    onSave,
    onDelete,
    loading = false,
}) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [useImperial, setUseImperial] = useState(true);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['basic']));
    const [formData, setFormData] = useState<ExtendedProfileData>({
        age: undefined,
        gender: undefined,
        height: undefined,
        weight: undefined,
        allergies: [],
        dietary_restrictions: [],
        likes: undefined,
        dislikes: undefined,
        notes: undefined,
    });
    const [allergiesText, setAllergiesText] = useState("");
    const [hasChanges, setHasChanges] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize form data when profile changes
    useEffect(() => {
        if (profile?.profile_data) {
            setFormData(profile.profile_data);
            setAllergiesText(profile.profile_data.allergies?.join(", ") || "");
        } else {
            // Reset to default values for new profiles
            setFormData({
                age: undefined,
                gender: undefined,
                height: undefined,
                weight: undefined,
                allergies: [],
                dietary_restrictions: [],
                likes: undefined,
                dislikes: undefined,
                notes: undefined,
            });
            setAllergiesText("");
        }
        setHasChanges(false);
    }, [profile]);

    const handleChange = (field: keyof ExtendedProfileData, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setHasChanges(true);
    };

    const handleAllergiesChange = (value: string) => {
        setAllergiesText(value);
        const allergiesArray = value
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s);
        handleChange("allergies", allergiesArray);
    };

    const handleArrayToggle = (field: keyof ExtendedProfileData, value: string) => {
        const current = (formData[field] as string[]) || [];
        const updated = current.includes(value)
            ? current.filter((item) => item !== value)
            : [...current, value];
        handleChange(field, updated);
    };

    const toggleSection = (section: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!hasChanges) return;

        setIsSubmitting(true);
        try {
            await onSave(formData);
            setHasChanges(false);
            
            // Show success toast and navigate to meal plan generation
            showToast({
                message: "Profile saved successfully! Ready to generate a meal plan?",
                type: "success",
                duration: 5000
            });
            
            // Navigate to meal plan generation tab with this profile selected
            navigate("/meal-planner/generate", { 
                state: { 
                    selectedProfileId: profile?.id,
                    profileName: profile?.name 
                } 
            });
        } catch (error) {
            console.error("Error saving profile:", error);
            showToast({
                message: "Failed to save profile. Please try again.",
                type: "error",
                duration: 4000
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = async () => {
        if (!onDelete || isMainProfile) return;

        const confirmed = window.confirm(
            `Are you sure you want to delete the profile "${profile?.name}"? This action cannot be undone.`
        );

        if (confirmed) {
            try {
                await onDelete();
            } catch (error) {
                console.error("Error deleting profile:", error);
            }
        }
    };

    // Convert between metric and imperial
    const convertHeight = (cm: number | undefined, toImperial: boolean) => {
        if (!cm) return { feet: '', inches: '' };
        if (toImperial) {
            const totalInches = cm / 2.54;
            const feet = Math.floor(totalInches / 12);
            const inches = Math.round(totalInches % 12);
            return { feet: feet.toString(), inches: inches.toString() };
        }
        return { feet: '', inches: '' };
    };

    const convertWeight = (kg: number | undefined, toImperial: boolean) => {
        if (!kg) return '';
        return toImperial ? Math.round(kg * 2.20462).toString() : kg.toString();
    };

    const SectionHeader = ({ title, section, required = false }: { title: string; section: string; required?: boolean }) => (
        <div
            onClick={() => toggleSection(section)}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                cursor: 'pointer',
                marginBottom: expandedSections.has(section) ? '16px' : '0',
                transition: 'all 0.2s ease',
                border: '1px solid #e2e8f0',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: '#1e293b' }}>
                    {title}
                </h3>
                {required && (
                    <span style={{
                        fontSize: '12px',
                        color: '#ef4444',
                        backgroundColor: '#fee2e2',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: 500,
                    }}>
                        Required
                    </span>
                )}
            </div>
            <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                    transform: expandedSections.has(section) ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                }}
            >
                <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#64748b"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );

    const InfoTooltip = ({ text }: { text: string }) => (
            <div
                style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#e0e7ff',
                color: '#4338ca',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'help',
                marginLeft: '4px',
            }}
            title={text}
        >
            ?
        </div>
    );

    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                padding: '24px',
                borderBottom: '1px solid #e5e7eb',
                background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                        <h2 style={{ fontSize: '24px', fontWeight: 700, margin: 0, color: '#1e293b' }}>
                        {profile?.name || "New Profile"}
                        {isMainProfile && (
                                <span style={{
                                    marginLeft: '12px',
                                    fontSize: '14px',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    padding: '4px 12px',
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                }}>
                                    Primary Profile
                            </span>
                        )}
                        </h2>
                        <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>
                            Build your personalized nutrition profile for AI-powered meal planning
                        </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {/* Unit Toggle */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            backgroundColor: 'white',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                        }}>
                            <span style={{ fontSize: '14px', color: '#64748b' }}>Metric</span>
                            <label style={{
                                position: 'relative',
                                display: 'inline-block',
                                width: '48px',
                                height: '24px',
                            }}>
                                <input
                                    type="checkbox"
                                    checked={useImperial}
                                    onChange={(e) => setUseImperial(e.target.checked)}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                />
                                <span style={{
                                    position: 'absolute',
                                    cursor: 'pointer',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: useImperial ? '#3b82f6' : '#cbd5e1',
                                    transition: '0.3s',
                                    borderRadius: '24px',
                                }}>
                                    <span style={{
                                        position: 'absolute',
                                        content: '',
                                        height: '18px',
                                        width: '18px',
                                        left: useImperial ? '26px' : '3px',
                                        bottom: '3px',
                                        backgroundColor: 'white',
                                        transition: '0.3s',
                                        borderRadius: '50%',
                                    }} />
                                </span>
                            </label>
                            <span style={{ fontSize: '14px', color: '#64748b' }}>Imperial</span>
                </div>
                {!isMainProfile && onDelete && (
                    <button
                        onClick={handleDeleteClick}
                        style={{
                                    background: '#fee2e2',
                                    border: 'none',
                                    color: '#dc2626',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                        }}
                        title="Delete Profile"
                    >
                                <span>🗑️</span> Delete
                    </button>
                )}
                    </div>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                {/* Basic Information Section - Always Required */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Basic Information" section="basic" required />
                    
                    {expandedSections.has('basic') && (
                        <div style={{ padding: '0 4px' }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: '20px',
                            }}>
                                {/* Age */}
                        <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                    fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '6px',
                                    }}>
                                        Age <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                            </label>
                            <NumberInput
                                min={0}
                                max={150}
                                value={formData.age ?? 0}
                                onChange={(val) => handleChange("age", val ? parseInt(val as any) : undefined)}
                                placeholder="Enter your age"
                                required
                            />
                        </div>

                                {/* Gender */}
                        <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                    fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '6px',
                                    }}>
                                        Gender <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                                        <InfoTooltip text="Biological sex affects nutritional needs and metabolism" />
                            </label>
                            <select
                                value={formData.gender || ""}
                                onChange={(e) => handleChange("gender", e.target.value || undefined)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    backgroundColor: 'white',
                                }}
                                required
                            >
                                <option value="">Select gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="non_binary">Non-binary</option>
                                <option value="prefer_not_to_say">Prefer not to say</option>
                            </select>
                        </div>

                                {/* Height */}
                        <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                    fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '6px',
                                    }}>
                                        Height {useImperial ? '(ft/in)' : '(cm)'} <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                            </label>
                                    {useImperial ? (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <NumberInput
                                                min={0}
                                                max={9}
                                                placeholder="Feet"
                                                value={parseInt(convertHeight(formData.height, true).feet) || 0}
                                                onChange={(val) => {
                                                    const feet = parseInt(val as any) || 0;
                                                    const inches = parseInt(convertHeight(formData.height, true).inches) || 0;
                                                    const cm = (feet * 12 + inches) * 2.54;
                                                    handleChange("height", cm);
                                                }}
                                                required
                                            />
                                            <NumberInput
                                                min={0}
                                                max={11}
                                                placeholder="Inches"
                                                value={parseInt(convertHeight(formData.height, true).inches) || 0}
                                                onChange={(val) => {
                                                    const feet = parseInt(convertHeight(formData.height, true).feet) || 0;
                                                    const inches = parseInt(val as any) || 0;
                                                    const cm = (feet * 12 + inches) * 2.54;
                                                    handleChange("height", cm);
                                                }}
                                            />
                                        </div>
                                    ) : (
                            <NumberInput
                                min={0}
                                max={300}
                                value={formData.height ?? 0}
                                onChange={(val) => handleChange("height", val ? parseFloat(val as any) : undefined)}
                                placeholder="Enter height in cm"
                                required
                            />
                                    )}
                        </div>

                                {/* Weight */}
                        <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                    fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '6px',
                                    }}>
                                        Weight {useImperial ? '(lbs)' : '(kg)'} <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                            </label>
                            <NumberInput
                                min={0}
                                max={useImperial ? 1000 : 500}
                                value={useImperial ? convertWeight(formData.weight, true) || 0 : formData.weight ?? 0}
                                onChange={(val) => {
                                    const value = parseFloat(val as any);
                                    if (useImperial) {
                                        handleChange("weight", value ? value / 2.20462 : undefined);
                                    } else {
                                        handleChange("weight", value || undefined);
                                    }
                                }}
                                placeholder={`Enter weight in ${useImperial ? 'lbs' : 'kg'}`}
                                required
                            />
                        </div>
                    </div>
                        </div>
                    )}
                </div>

                {/* Activity & Fitness Section */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Activity & Fitness" section="activity" />
                    
                    {expandedSections.has('activity') && (
                        <div style={{ padding: '0 4px' }}>
                            {/* Activity Level */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Activity Level
                                    <InfoTooltip text="Your daily activity level affects caloric needs" />
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                    {[
                                        { value: 'sedentary', label: 'Sedentary', desc: 'Little to no exercise' },
                                        { value: 'lightly_active', label: 'Lightly Active', desc: 'Exercise 1-3 days/week' },
                                        { value: 'moderately_active', label: 'Moderately Active', desc: 'Exercise 3-5 days/week' },
                                        { value: 'very_active', label: 'Very Active', desc: 'Exercise 6-7 days/week' },
                                        { value: 'extremely_active', label: 'Extremely Active', desc: 'Physical job or 2x daily training' },
                                    ].map(option => (
                    <label
                                            key={option.value}
                        style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                padding: '12px',
                                                border: `2px solid ${formData.activity_level === option.value ? '#3b82f6' : '#e2e8f0'}`,
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                backgroundColor: formData.activity_level === option.value ? '#eff6ff' : 'white',
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name="activity_level"
                                                value={option.value}
                                                checked={formData.activity_level === option.value}
                                                onChange={(e) => handleChange('activity_level', e.target.value)}
                                                style={{ marginRight: '12px', marginTop: '2px' }}
                                            />
                                            <div>
                                                <div style={{ fontWeight: 500, color: '#1e293b' }}>{option.label}</div>
                                                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{option.desc}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Exercise Types */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                            fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Types of Exercise
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {[
                                        'Cardio', 'Weight Training', 'HIIT', 'Yoga', 'Pilates', 
                                        'Running', 'Cycling', 'Swimming', 'CrossFit', 'Martial Arts',
                                        'Team Sports', 'Walking', 'Dancing', 'Rock Climbing'
                                    ].map(type => (
                                        <label
                                            key={type}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '8px 16px',
                                                border: `1px solid ${formData.exercise_types?.includes(type) ? '#3b82f6' : '#e2e8f0'}`,
                                                borderRadius: '20px',
                                                cursor: 'pointer',
                                                backgroundColor: formData.exercise_types?.includes(type) ? '#eff6ff' : 'white',
                                                fontSize: '14px',
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={formData.exercise_types?.includes(type) || false}
                                                onChange={() => handleArrayToggle('exercise_types', type)}
                                                style={{ display: 'none' }}
                                            />
                                            {type}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Fitness Goals */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Fitness Goals
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                                    {[
                                        'Weight Loss', 'Muscle Gain', 'Maintain Weight', 'Improve Endurance',
                                        'Increase Strength', 'Better Health', 'Athletic Performance', 'Body Recomposition'
                                    ].map(goal => (
                                        <label key={goal} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.fitness_goals?.includes(goal) || false}
                                                onChange={() => handleArrayToggle('fitness_goals', goal)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            {goal}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Dietary Restrictions & Allergies Section */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Dietary Restrictions & Allergies" section="dietary" />
                    
                    {expandedSections.has('dietary') && (
                        <div style={{ padding: '0 4px' }}>
                            {/* Allergies */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Allergies & Intolerances
                                    <InfoTooltip text="List all food allergies and intolerances for safe meal planning" />
                    </label>
                    <input
                        type="text"
                                    placeholder="Enter allergies separated by commas (e.g., peanuts, shellfish, lactose)"
                        value={allergiesText}
                        onChange={(e) => handleAllergiesChange(e.target.value)}
                        style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                    }}
                                />
                                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {['Peanuts', 'Tree Nuts', 'Milk', 'Eggs', 'Soy', 'Wheat', 'Fish', 'Shellfish', 'Sesame'].map(allergy => (
                                        <button
                                            key={allergy}
                                            type="button"
                                            onClick={() => {
                                                const current = allergiesText ? allergiesText.split(',').map(s => s.trim()) : [];
                                                if (!current.includes(allergy)) {
                                                    handleAllergiesChange(current.concat(allergy).join(', '));
                                                }
                                            }}
                        style={{
                                                padding: '4px 12px',
                                                fontSize: '12px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '16px',
                                                backgroundColor: 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            + {allergy}
                                        </button>
                                    ))}
                                </div>
                </div>

                {/* Dietary Restrictions */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                            fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Dietary Preferences & Restrictions
                    </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                                    {[
                                        { value: 'vegan', label: 'Vegan', icon: '🌱' },
                                        { value: 'vegetarian', label: 'Vegetarian', icon: '🥗' },
                                        { value: 'pescatarian', label: 'Pescatarian', icon: '🐟' },
                                        { value: 'dairy_free', label: 'Dairy Free', icon: '🥛' },
                                        { value: 'gluten_free', label: 'Gluten Free', icon: '🌾' },
                                        { value: 'keto', label: 'Keto', icon: '🥑' },
                                        { value: 'paleo', label: 'Paleo', icon: '🍖' },
                                        { value: 'low_carb', label: 'Low Carb', icon: '🍞' },
                                        { value: 'low_sodium', label: 'Low Sodium', icon: '🧂' },
                                        { value: 'diabetic', label: 'Diabetic Friendly', icon: '💉' },
                                        { value: 'kosher', label: 'Kosher', icon: '✡️' },
                                        { value: 'halal', label: 'Halal', icon: '☪️' },
                                    ].map(option => (
                            <label
                                key={option.value}
                                style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '10px',
                                                border: `1px solid ${formData.dietary_restrictions?.includes(option.value) ? '#3b82f6' : '#e2e8f0'}`,
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                backgroundColor: formData.dietary_restrictions?.includes(option.value) ? '#eff6ff' : 'white',
                                                transition: 'all 0.2s',
                                }}
                            >
                                <input
                                    type="checkbox"
                                                checked={formData.dietary_restrictions?.includes(option.value) || false}
                                                onChange={() => handleArrayToggle('dietary_restrictions', option.value)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            <span style={{ marginRight: '6px' }}>{option.icon}</span>
                                            <span style={{ fontSize: '14px' }}>{option.label}</span>
                            </label>
                        ))}
                    </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Food Preferences & Cuisines Section */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Food Preferences & Cuisines" section="preferences" />
                    
                    {expandedSections.has('preferences') && (
                        <div style={{ padding: '0 4px' }}>
                            {/* Cuisine Preferences */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Favorite Cuisines
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {[
                                        'Italian', 'Mexican', 'Chinese', 'Japanese', 'Thai', 'Indian', 
                                        'Mediterranean', 'French', 'American', 'Greek', 'Korean', 'Vietnamese',
                                        'Spanish', 'Middle Eastern', 'Ethiopian', 'Caribbean', 'Brazilian'
                                    ].map(cuisine => (
                                        <label
                                            key={cuisine}
                        style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '8px 16px',
                                                border: `1px solid ${formData.cuisine_preferences?.includes(cuisine) ? '#3b82f6' : '#e2e8f0'}`,
                                                borderRadius: '20px',
                                                cursor: 'pointer',
                                                backgroundColor: formData.cuisine_preferences?.includes(cuisine) ? '#eff6ff' : 'white',
                                                fontSize: '14px',
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={formData.cuisine_preferences?.includes(cuisine) || false}
                                                onChange={() => handleArrayToggle('cuisine_preferences', cuisine)}
                                                style={{ display: 'none' }}
                                            />
                                            {cuisine}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Flavor Preferences */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Flavor Preferences
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                                    {['Sweet', 'Salty', 'Spicy', 'Sour', 'Umami', 'Bitter', 'Mild', 'Bold'].map(flavor => (
                                        <label key={flavor} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.flavor_preferences?.includes(flavor) || false}
                                                onChange={() => handleArrayToggle('flavor_preferences', flavor)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            {flavor}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Likes and Dislikes */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                    fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '6px',
                                    }}>
                                        Foods You Love
                            </label>
                            <textarea
                                value={formData.likes || ""}
                                onChange={(e) => handleChange("likes", e.target.value || undefined)}
                                placeholder="List foods, ingredients, or dishes you enjoy..."
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                }}
                            />
                        </div>

                        <div>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                    fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '6px',
                                    }}>
                                        Foods to Avoid
                            </label>
                            <textarea
                                value={formData.dislikes || ""}
                                onChange={(e) => handleChange("dislikes", e.target.value || undefined)}
                                placeholder="List foods, ingredients, or dishes you prefer to avoid..."
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                }}
                            />
                        </div>
                    </div>
                        </div>
                    )}
                </div>

                                {/* Meal Planning Preferences Section */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Meal Planning Preferences" section="mealplanning" />
                    
                    {expandedSections.has('mealplanning') && (
                        <div style={{ padding: '0 4px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                {/* Cost Preference - Left Column */}
                                <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: '#374151',
                                        marginBottom: '8px',
                                    }}>
                                        Cost Preference
                                        <InfoTooltip text="Guide meal planning based on your budget preferences" />
                                    </label>
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {[
                                            { value: 'budget', label: 'Budget Friendly', desc: 'Prioritize affordable ingredients', icon: '💵' },
                                            { value: 'balanced', label: 'Balanced', desc: 'Mix of budget and quality options', icon: '⚖️' },
                                            { value: 'premium', label: 'Premium', desc: 'Quality over cost considerations', icon: '💎' },
                                        ].map(option => (
                                            <label
                                                key={option.value}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    padding: '12px',
                                                    border: `2px solid ${formData.cost_preference === option.value ? '#3b82f6' : '#e2e8f0'}`,
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    backgroundColor: formData.cost_preference === option.value ? '#eff6ff' : 'white',
                                                    transition: 'all 0.2s',
                                                }}
                                            >
                                                <input
                                                    type="radio"
                                                    name="cost_preference"
                                                    value={option.value}
                                                    checked={formData.cost_preference === option.value}
                                                    onChange={(e) => handleChange('cost_preference', e.target.value)}
                                                    style={{ marginRight: '12px', marginTop: '2px' }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span>{option.icon}</span>
                                                        <span style={{ fontWeight: 500, color: '#1e293b' }}>{option.label}</span>
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>{option.desc}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Cooking Time & Skill - Right Column */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                    {/* Cooking time */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#374151',
                                            marginBottom: '6px',
                                        }}>
                                            Available Cooking Time
                                        </label>
                                        <select
                                            value={formData.cooking_time_available || ""}
                                            onChange={(e) => handleChange("cooking_time_available", e.target.value || undefined)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 14px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                backgroundColor: 'white',
                                            }}
                                        >
                                            <option value="">Select cooking time</option>
                                            <option value="minimal">Minimal (&lt; 15 min/meal)</option>
                                            <option value="moderate">Moderate (15-30 min/meal)</option>
                                            <option value="extensive">Extensive (30+ min/meal)</option>
                                        </select>
                                    </div>

                                    {/* Cooking skill */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#374151',
                                            marginBottom: '6px',
                                        }}>
                                            Cooking Skill Level
                                        </label>
                                        <select
                                            value={formData.cooking_skill_level || ""}
                                            onChange={(e) => handleChange("cooking_skill_level", e.target.value || undefined)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 14px',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                backgroundColor: 'white',
                                            }}
                                        >
                                            <option value="">Select skill level</option>
                                            <option value="beginner">Beginner (Basic recipes)</option>
                                            <option value="intermediate">Intermediate (Comfortable cooking)</option>
                                            <option value="advanced">Advanced (Complex recipes welcome)</option>
                                            <option value="chef">Chef level (Bring it on!)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Macro Nutrient Targets Section */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Macro Nutrient Targets" section="macros" />
                    
                    {expandedSections.has('macros') && (
                        <div style={{ padding: '0 4px' }}>
                            <MacroTargeting 
                                onChange={(targets) => handleChange('macro_targets', targets)}
                                initialValues={formData.macro_targets}
                            />
                        </div>
                    )}
                </div>

                {/* Health & Medical Section */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Health & Medical Information" section="health" />
                    
                    {expandedSections.has('health') && (
                        <div style={{ padding: '0 4px' }}>
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#fef3c7',
                                borderRadius: '8px',
                                marginBottom: '16px',
                                fontSize: '13px',
                                color: '#92400e',
                            }}>
                                ⚠️ This information helps create safer, more personalized meal plans. Always consult healthcare providers for medical advice.
                            </div>

                            {/* Health Conditions */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '8px',
                                }}>
                                    Health Conditions (Optional)
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                                    {[
                                        'Diabetes Type 1', 'Diabetes Type 2', 'High Blood Pressure', 'High Cholesterol',
                                        'Heart Disease', 'PCOS', 'Thyroid Issues', 'IBS', 'Crohns Disease',
                                        'Celiac Disease', 'Kidney Disease', 'Liver Disease', 'Gout', 'Arthritis'
                                    ].map(condition => (
                                        <label key={condition} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.health_conditions?.includes(condition) || false}
                                                onChange={() => handleArrayToggle('health_conditions', condition)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            {condition}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Supplements */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#374151',
                                    marginBottom: '6px',
                                }}>
                                    Supplements & Vitamins (Optional)
                                </label>
                                <input
                                    type="text"
                                    placeholder="List any supplements you take regularly..."
                                    value={formData.supplements?.join(', ') || ''}
                                    onChange={(e) => handleChange('supplements', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Additional Notes */}
                <div style={{ marginBottom: '24px' }}>
                    <SectionHeader title="Additional Information" section="additional" />
                    
                    {expandedSections.has('additional') && (
                        <div style={{ padding: '0 4px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#374151',
                                marginBottom: '6px',
                            }}>
                        Additional Notes
                    </label>
                    <textarea
                        value={formData.notes || ""}
                        onChange={(e) => handleChange("notes", e.target.value || undefined)}
                        placeholder="Any other information that might help create your perfect meal plan..."
                        rows={4}
                        style={{
                            width: '100%',
                            padding: '10px 14px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            resize: 'vertical',
                        }}
                    />
                </div>
                )}
            </div>

                {/* Save Button */}
                {hasChanges && (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                    }}>
                        <div style={{ fontSize: '14px', color: '#64748b' }}>
                            You have unsaved changes
                        </div>
                        <button
                            type="submit"
                            disabled={isSubmitting || loading}
                            style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                padding: '12px 32px',
                                borderRadius: '8px',
                                border: 'none',
                                fontSize: '16px',
                                fontWeight: 600,
                                cursor: isSubmitting || loading ? 'not-allowed' : 'pointer',
                                opacity: isSubmitting || loading ? 0.6 : 1,
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                            }}
                        >
                            {isSubmitting ? 'Saving...' : 'Save Profile'}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default ProfileForm;
